import { Link } from 'react-router-dom';

import { SoftCard } from '@/components/ui/soft-card';

/**
 * Edge-to-edge table inside a SoftCard. Used wherever the Lovable design
 * shows a "Product / Views / Clicks"-style mini table — popular products,
 * search terms, locations, clicks-to-sales.
 *
 * `columns` is an array of `{ key, label, width?, render? }`. The first
 * column is left-aligned and flex-grows; subsequent columns are right-
 * aligned and use their `width` as a fixed track. The last column is bolded
 * as the table's headline metric, matching the Lovable design.
 */
export function ColumnTable({ rows, columns, emptyLabel, viewMoreLabel = 'View more', viewMoreHref, onViewMore }) {
	const widths = columns.map((c) => c.width || '');
	// `minmax(0, 1fr)` lets the flexible track shrink below its min-content size.
	// Default `1fr` is `minmax(auto, 1fr)`, so a long unbroken cell would blow
	// the column wider than its grid track, throwing side-by-side cards out of
	// alignment when search terms or product names ran long.
	const template = `minmax(0, 1fr) ${widths.slice(1).join(' ')}`;
	return (
		<SoftCard className="overflow-hidden p-0">
			<div
				className="grid gap-6 border-b border-border/60 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
				style={{ gridTemplateColumns: template }}
			>
				{columns.map((c, i) => (
					<div key={c.key} className={i === 0 ? '' : 'text-right'}>
						{c.label}
					</div>
				))}
			</div>
			<div className="divide-y divide-border/60">
				{rows.length === 0 ? (
					<div className="px-5 py-6 text-center text-sm text-muted-foreground">{emptyLabel ?? 'No data yet.'}</div>
				) : (
					rows.map((row, i) => (
						<div key={row.id ?? i} className="grid items-center gap-6 px-5 py-3.5" style={{ gridTemplateColumns: template }}>
							{columns.map((c, idx) => {
								let cellClass;
								if (idx === 0) {
									cellClass = 'line-clamp-1 text-sm font-medium text-foreground';
								} else if (idx === columns.length - 1) {
									cellClass = 'text-right text-sm font-semibold tabular-nums text-foreground';
								} else {
									cellClass = 'text-right text-sm tabular-nums text-foreground';
								}
								return (
									<div key={c.key} className={cellClass}>
										{c.render ? c.render(row) : row[c.key]}
									</div>
								);
							})}
						</div>
					))
				)}
			</div>
			{rows.length > 0 && (viewMoreHref || onViewMore) ? (
				<div className="border-t border-border/60 px-5 py-3 text-center">
					{viewMoreHref ? (
						<Link to={viewMoreHref} className="text-sm font-semibold text-primary transition hover:underline">
							{viewMoreLabel}
						</Link>
					) : (
						<button
							type="button"
							onClick={onViewMore}
							className="text-sm font-semibold text-primary transition hover:underline"
						>
							{viewMoreLabel}
						</button>
					)}
				</div>
			) : null}
		</SoftCard>
	);
}
